'use client';

import React, { useEffect, useRef, useState } from 'react';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import Image from '@components/common/ImageFallback';
import Link from 'next/link';
import { searchProduct } from '@services/client/product';
import { useTranslation } from '@root/src/i18n/client';
import { convertCurrency } from '@root/src/lib/format';

interface ResultsType {
  slug: string;
  image: string[] | string;
  name: string;
  retailPrice?: number | string;
  price?: number | string;
  categoryName?: string;
  categorySlug?: string;
}

const normalizeSearchResults = (payload: any): ResultsType[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }
  if (Array.isArray(payload?.results)) {
    return payload.results;
  }
  return [];
};

export default function HeaderSearchMobile({
  desktop = false,
}: {
  desktop?: boolean;
}) {
  const { t } = useTranslation('common');
  const [results, setResults] = useState<ResultsType[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);

  async function searchProductAll(name: string) {
    const response: any = await searchProduct(name);
    const normalizedResults = normalizeSearchResults(response);
    const uniqueResults = normalizedResults.reduce((acc: ResultsType[], item: any) => {
      if (!acc.find(existing => existing.slug === item?.slug)) {
        acc.push(item);
      }
      return acc;
    }, []);

    setResults(uniqueResults);
  }

  useEffect(() => {
    const keyword = inputValue.trim();

    if (keyword) {
      searchProductAll(keyword);
    } else {
      setResults([]);
    }
  }, [inputValue]);

  useEffect(() => {
    if (!isSearchVisible) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchVisible]);

  // Clear input and results when the search panel is closed
  useEffect(() => {
    if (!isSearchVisible) {
      setInputValue('');
      setResults([]);
    }
  }, [isSearchVisible]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsSearchVisible(prev => !prev)}
        className={`flex items-center justify-center rounded-full border border-[#ddcfb7] bg-[#fffaf3] text-[#5e574b] transition hover:border-[#667348] hover:text-[#667348] ${
          desktop ? 'h-10 w-10' : 'h-9 w-9'
        }`}
        aria-label={t('txtSearchProductPlaceButton')}
      >
        <SearchOutlined className={desktop ? 'text-[18px]' : 'text-[17px]'} />
      </button>

      {isSearchVisible && (
        <div className="fixed inset-x-0 top-[56px] z-[90] border-t border-[#e7dcc8] bg-[rgba(34,58,39,0.14)] backdrop-blur-[2px] md:top-[72px]">
          <div ref={searchRef} className="mx-auto w-full max-w-[1240px] px-3 py-3 md:px-6">
            <div className="rounded-[28px] border border-[#e7dcc8] bg-[#fffaf4] p-4 shadow-[0_24px_60px_rgba(91,77,56,0.14)] md:p-6">
              <div className="mb-4 flex items-center gap-3 rounded-[20px] border border-[#e2d4bf] bg-white px-4 py-3">
                <SearchOutlined className="text-[18px] text-[#667348]" />
                <input
                  type="text"
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  placeholder={t('txtSearchProductPlaceHolder')}
                  className="w-full appearance-none bg-transparent text-[15px] text-[#4d463b] placeholder:text-[#9c9486] focus:outline-none"
                />
                <button
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-[#d9ccb8] text-[#7b7264] transition hover:border-[#223A27] hover:text-[#223A27]"
                  onClick={() => setIsSearchVisible(false)}
                  type="button"
                >
                  <CloseOutlined />
                </button>
              </div>

              <div className="max-h-[65vh] overflow-auto">
                {inputValue.trim() && results.length === 0 ? (
                  <div className="rounded-[22px] border border-dashed border-[#decfb7] bg-[#fcf8f1] px-4 py-10 text-center text-sm text-[#7a7265]">
                    {t('lytra.noMatchingProducts')}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                    {results.map((item, index) => (
                      <div
                        className="rounded-[22px] border border-[#e7dcc8] bg-white p-3 transition hover:-translate-y-1 hover:shadow-[0_16px_30px_rgba(91,77,56,0.08)]"
                        key={index}
                      >
                        <Link
                          href={`/${item?.slug}`}
                          onClick={() => setIsSearchVisible(false)}
                          className="block"
                        >
                          <Image
                            width={500}
                            height={260}
                            src={Array.isArray(item?.image) ? item.image[0] : item?.image}
                            alt={item?.name}
                            title={item?.name}
                            className="h-[140px] w-full rounded-[18px] object-cover md:h-[180px]"
                          />
                          <h3 className="mt-3 line-clamp-2 min-h-[2.8rem] text-center text-[14px] font-medium text-[#4d463b] md:text-[15px]">
                            {item?.name}
                          </h3>
                          <h4 className="mt-2 text-center text-[13px] font-semibold text-[#9d1d22] md:text-[14px]">
                            {convertCurrency(Number(item?.retailPrice ?? item?.price ?? 0))}
                          </h4>
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
